import { isSport } from "../../common/sportFunctions.ts";
import { PLAYER } from "../../common/constants.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import { getCols } from "../../common/getCols.ts";
import { helpers } from "../util/helpers.ts";
import { wrappedPlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { wrappedContractAmount, wrappedContractExp } from "../components/contract.tsx";
import { PHASE } from "../../common/constants.ts";
import useTitleBar from "../hooks/useTitleBar.tsx";
import type { View } from "../../common/types.ts";
import { SummaryTeam } from "./Trade/Summary.tsx";
import { toWorker } from "../util/toWorker.ts";
import { useLocal } from "../util/local.ts";
import {
	Offer,
	OfferTable,
	type OfferType,
	pickScore,
	playerScore,
} from "./TradingBlock/index.tsx";
import { useEffect, useState } from "react";
import { ActionButton } from "../components/ActionButton.tsx";
import useTradeOffersSwitch from "../hooks/useTradeOffersSwitch.tsx";
import LookingFor from "./TradingBlock/LookingFor.tsx";
import useLookingForState from "./TradingBlock/useLookingForState.ts";
import { SafeHtml } from "../components/SafeHtml.tsx";

const TradeProposals = ({ offers, proposalPlayers, proposalPicks, proposalStats, selectedPids, selectedDpids, userTid, tradeProposalsAvailable, tradeProposalsAvailableDpids, tradeProposalsLookingFor, tradeProposalsOffered, offerMode: savedMode, offeredPids: savedOfferedPids, offeredDpids: savedOfferedDpids }: View<"tradeProposals">) => {
	const [availablePids, setAvailablePids] = useState(selectedPids);
	const [availableDpids, setAvailableDpids] = useState(selectedDpids);
	const [lookingForState, setLookingForState, resetLookingForState] =
		useLookingForState(tradeProposalsLookingFor[userTid]);
	const [lastLookingForState, setLastLookingForState] = useState(lookingForState);
	const lookingForChanged = JSON.stringify(lookingForState) !== JSON.stringify(lastLookingForState);
	const [tab, setTab] = useState<"roster" | "prospects" | "depth">("roster");
	const groups = {
		roster: proposalPlayers.filter((p) => p.tid !== PLAYER.MINORS),
		prospects: proposalPlayers.filter((p) => p.tid === PLAYER.MINORS && p.prospect),
		depth: proposalPlayers.filter((p) => p.tid === PLAYER.MINORS && !p.prospect),
	};
	const tabLabels = { roster: "Roster", prospects: "Prospects", depth: "Minors depth" };
	// The worker sends new arrays on every update; only reset the checkboxes when the saved selection changes
	const selectedPidsKey = JSON.stringify(selectedPids);
	const selectedDpidsKey = JSON.stringify(selectedDpids);
	useEffect(() => {
		setAvailablePids(selectedPids);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPidsKey]);
	useEffect(() => {
		setAvailableDpids(selectedDpids);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDpidsKey]);
	// What goes to every team: the whole package, or only the part picked here
	const [mode, setMode] = useState(savedMode);
	const [offeredPids, setOfferedPids] = useState(savedOfferedPids);
	const [offeredDpids, setOfferedDpids] = useState(savedOfferedDpids);
	const savedOfferKey = JSON.stringify([savedMode, savedOfferedPids, savedOfferedDpids]);
	useEffect(() => {
		setMode(savedMode);
		setOfferedPids(savedOfferedPids);
		setOfferedDpids(savedOfferedDpids);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [savedOfferKey]);
	const sortedKey = (ids: number[]) => JSON.stringify([...ids].sort((a, b) => a - b));
	const offerChanged = mode !== savedMode || (mode === "pick" &&
		(sortedKey(offeredPids) !== sortedKey(savedOfferedPids) ||
			sortedKey(offeredDpids) !== sortedKey(savedOfferedDpids)));
	const selectionChanged = offerChanged || JSON.stringify([...availablePids].sort((a, b) => a - b)) !==
		JSON.stringify([...selectedPids].sort((a, b) => a - b)) ||
		JSON.stringify([...availableDpids].sort((a, b) => a - b)) !==
		JSON.stringify([...selectedDpids].sort((a, b) => a - b));
	const {
		challengeNoRatings,
		challengeNoTrades,
		gameOver,
		phase,
		salaryCap,
		salaryCapType,
		spectator,
		teamInfoCache,
	} = useLocal([
		"challengeNoRatings",
		"challengeNoTrades",
		"gameOver",
		"phase",
		"salaryCap",
		"salaryCapType",
		"spectator",
		"teamInfoCache",
	]);

	const [removedTids, setRemovedTids] = useState<number[]>([]);
	const [prevOffers, setPrevOffers] = useState(offers);

	// Without this, we'd still see the old offers even after 10 games are played and there are new offers
	useEffect(() => {
		const tids = JSON.stringify(offers.map((offer) => offer.tid).sort());
		const prevTids = JSON.stringify(
			prevOffers.map((offer) => offer.tid).sort(),
		);

		if (tids !== prevTids || offers !== prevOffers) {
			setRemovedTids([]);
			setPrevOffers(offers);
		}
	}, [offers, prevOffers]);

	useTitleBar({ title: "Trade Proposals" });

	const [refreshing, setRefreshing] = useState(false);

	const tradeOffersSwitch = useTradeOffersSwitch();

	if (spectator) {
		return <p>You're not allowed to make trades in spectator mode.</p>;
	}

	if (challengeNoTrades) {
		return (
			<div>
				<p className="alert alert-danger d-inline-block">
					<b>Challenge Mode:</b> You're not allowed to make trades.
				</p>
			</div>
		);
	}

	if (
		(phase >= PHASE.AFTER_TRADE_DEADLINE && phase <= PHASE.PLAYOFFS) ||
		phase === PHASE.FANTASY_DRAFT ||
		gameOver
	) {
		return (
			<div>
				<h2>Error</h2>
				<p>
					You're not allowed to make trades{" "}
					{phase === PHASE.AFTER_TRADE_DEADLINE
						? "after the trade deadline"
						: "now"}
					.
				</p>
			</div>
		);
	}

	const handleNegotiate = async (offer: {
		dpids: number[];
		dpidsUser: number[];
		pids: number[];
		pidsUser: number[];
		tid: number;
	}) => {
		await toWorker("actions", "tradeFor", {
			otherDpids: offer.dpids,
			otherPids: offer.pids,
			tid: offer.tid,
			userDpids: offer.dpidsUser,
			userPids: offer.pidsUser,
		});
	};

	const getSearchSortValues = (
		players: OfferType["players"],
		picks: OfferType["picks"],
	) => {
		return {
			searchValue: `${players
				.map((p) => `${p.name} ${p.ratings.pos}`)
				.join(" ")} ${picks.map((pick) => pick.desc).join(" ")}`,
			sortValue: playerScore(players) + pickScore(picks),
		};
	};

	const filteredOffers = offers.filter(
		(offer) => !removedTids.includes(offer.tid) && (!isSport("baseball") || (!selectionChanged && !lookingForChanged)),
	);

	return (
		<>
			{isSport("baseball") ? (
				<section className="mb-3">
					<p>Marque os jogadores e as picks negociáveis e escolha o que oferecer: todos os times que quiserem fazem proposta.</p>
					<ul className="nav nav-tabs mb-2">
						{(["roster", "prospects", "depth"] as const).map((key) => {
							const selected = groups[key].filter((p) => availablePids.includes(p.pid)).length;
							return <li className="nav-item" key={key}>
								<button type="button" className={"nav-link" + (tab === key ? " active" : "")} onClick={() => setTab(key)}>
									{tabLabels[key]} ({groups[key].length})
									{selected > 0 ? <span className="badge bg-success ms-1">{selected}</span> : null}
								</button>
							</li>;
						})}
					</ul>
					<div className="d-flex flex-wrap gap-2 align-items-center mb-2">
						<button className="btn btn-light-bordered btn-sm" disabled={refreshing} onClick={() => setAvailablePids((prev) => [...new Set([...prev, ...groups[tab].map((p) => p.pid)])])}>Marcar aba</button>
						<button className="btn btn-light-bordered btn-sm" disabled={refreshing} onClick={() => setAvailablePids((prev) => prev.filter((pid) => !groups[tab].some((p) => p.pid === pid)))}>Desmarcar aba</button>
						<span>{availablePids.length} jogadores e {availableDpids.length} picks negociáveis</span>
					</div>
					<DataTable
						cols={getCols(["", "Name", "Pos", "Age", "Ovr", "Pot", "Contract", "Exp", ...proposalStats.map((stat) => `stat:${stat}`)], {
							"": { noSearch: true, sortSequence: [], width: "1%" },
						})}
						defaultSort={[6, "desc"]}
						defaultStickyCols={window.mobile ? 1 : 2}
						name={tab === "roster" ? "TradeProposals:Roster" : "TradeProposals:Minors"}
						rows={groups[tab].map((p) => ({
							key: p.pid,
							data: [
								<input className="form-check-input" type="checkbox" aria-label={"Negociável: " + p.firstName + " " + p.lastName} disabled={refreshing} checked={availablePids.includes(p.pid)} onChange={() => setAvailablePids((prev) => prev.includes(p.pid) ? prev.filter((pid) => pid !== p.pid) : [...prev, p.pid])} />,
								wrappedPlayerNameLabels({
									pid: p.pid, injury: p.injury, jerseyNumber: p.jerseyNumber,
									skills: p.ratings.skills, defaultWatch: p.watch,
									firstName: p.firstName, firstNameShort: p.firstNameShort, lastName: p.lastName,
								}),
								p.ratings.pos, p.age,
								!challengeNoRatings ? p.ratings.ovr : null,
								!challengeNoRatings ? p.ratings.pot : null,
								wrappedContractAmount(p), wrappedContractExp(p),
								...proposalStats.map((stat) => helpers.roundStat(p.stats[stat], stat)),
							],
						}))}
					/>
					<div className="mt-4">
						<h3>Draft Picks</h3>
						<div className="d-flex gap-2 mb-2">
							<button className="btn btn-light-bordered btn-sm" disabled={refreshing} onClick={() => setAvailableDpids(proposalPicks.map((dp) => dp.dpid))}>Marcar picks</button>
							<button className="btn btn-light-bordered btn-sm" disabled={refreshing} onClick={() => setAvailableDpids([])}>Desmarcar picks</button>
						</div>
						<DataTable
							cols={getCols(["", "Draft Picks"], { "": { sortSequence: [] }, "Draft Picks": { width: "100%" } })}
							defaultSort={[1, "asc"]}
							hideAllControls
							hideMenuToo
							name="TradeProposals:Picks"
							rows={proposalPicks.map((pick, i) => ({
								key: pick.dpid,
								data: [
									<input className="form-check-input" type="checkbox" aria-label={"Negociável: pick " + pick.dpid} disabled={refreshing} checked={availableDpids.includes(pick.dpid)} onChange={() => setAvailableDpids((prev) => prev.includes(pick.dpid) ? prev.filter((dpid) => dpid !== pick.dpid) : [...prev, pick.dpid])} />,
									{ value: <SafeHtml dirty={pick.desc} />, searchValue: pick.desc, sortValue: i },
								],
							}))}
						/>
					</div>
					<div className="mt-4">
						<h3>Pacote oferecido</h3>
						<div className="btn-group mb-2" role="group">
							{([["whole", "Pacote inteiro"], ["pick", "Escolher do pacote"], ["ai", "A IA escolhe"]] as const).map(([key, label]) => (
								<button key={key} type="button" className={"btn btn-sm " + (mode === key ? "btn-primary" : "btn-light-bordered")} disabled={refreshing} onClick={() => setMode(key)}>{label}</button>
							))}
						</div>
						<p className="mb-2">{mode === "whole"
							? "Todos os negociáveis vão juntos para todos os times. Cada time que quiser o pacote diz o que daria."
							: mode === "pick"
								? "Só os escolhidos abaixo vão para todos os times. Escolha um só jogador para ver todos os times que o querem."
								: "Cada time pega dos negociáveis só os que mais precisa (de 1 a 4) e diz o que daria por eles. Cada proposta pode ter um pacote diferente."}</p>
						{mode === "pick" ? (
							<div className="d-flex flex-wrap gap-1">
								{availablePids.map((pid) => {
									const p = proposalPlayers.find((p) => p.pid === pid);
									if (!p) {
										return null;
									}
									const on = offeredPids.includes(pid);
									return <button key={pid} type="button" className={"btn btn-sm " + (on ? "btn-success" : "btn-light-bordered")} disabled={refreshing} onClick={() => setOfferedPids((prev) => on ? prev.filter((x) => x !== pid) : [...prev, pid])}>
										{p.firstName} {p.lastName} {p.ratings.pos} {!challengeNoRatings ? p.ratings.ovr : null}
									</button>;
								})}
								{availableDpids.map((dpid) => {
									const pick = proposalPicks.find((dp) => dp.dpid === dpid);
									if (!pick) {
										return null;
									}
									const on = offeredDpids.includes(dpid);
									return <button key={"dp" + dpid} type="button" className={"btn btn-sm " + (on ? "btn-success" : "btn-light-bordered")} disabled={refreshing} onClick={() => setOfferedDpids((prev) => on ? prev.filter((x) => x !== dpid) : [...prev, dpid])}>
										{pick.desc.replace(/<[^>]+>/g, "")}
									</button>;
								})}
								{availablePids.length === 0 && availableDpids.length === 0 ? <span>Marque negociáveis acima primeiro.</span> : null}
							</div>
						) : null}
					</div>
					<div className="mt-4">
						<LookingFor disabled={refreshing} state={lookingForState} setState={setLookingForState} />
						<button className="btn btn-light-bordered btn-sm mt-2" disabled={refreshing} onClick={resetLookingForState}>Limpar filtros</button>
					</div>
					{selectionChanged || lookingForChanged ? <p className="mt-2 mb-0">Clique em buscar propostas para salvar esta seleção e os filtros.</p> : selectedPids.length === 0 && selectedDpids.length === 0 ? <p className="mt-2 mb-0">Selecione pelo menos um jogador ou uma pick para receber propostas.</p> : null}
				</section>
			) : (
				<p>These are trade proposals from up to 5 AI teams. New teams will appear here every 10 games.</p>
			)}
			<ActionButton
				onClick={async () => {
					setRefreshing(true);
					try {
						if (isSport("baseball")) {
							await toWorker("main", "updateGameAttributes", {
								tradeProposalsAvailable: { ...tradeProposalsAvailable, [userTid]: availablePids },
								tradeProposalsAvailableDpids: { ...tradeProposalsAvailableDpids, [userTid]: availableDpids },
								tradeProposalsLookingFor: { ...tradeProposalsLookingFor, [userTid]: lookingForState },
								tradeProposalsOffered: (() => {
									const next = { ...tradeProposalsOffered };
									if (mode === "whole") {
										delete next[userTid];
									} else if (mode === "ai") {
										next[userTid] = { pids: [], dpids: [], aiChooses: true };
									} else {
										next[userTid] = {
											pids: offeredPids.filter((pid) => availablePids.includes(pid)),
											dpids: offeredDpids.filter((dpid) => availableDpids.includes(dpid)),
										};
									}
									return next;
								})(),
							});
						}
						await toWorker("main", "incrementTradeProposalsSeed", undefined);
						setLastLookingForState(lookingForState);
					} finally {
						setRefreshing(false);
					}
					setRefreshing(false);
				}}
				processing={refreshing}
				processingText="Loading"
				variant="secondary"
			>
				{isSport("baseball") ? "Buscar propostas para os negociáveis" : "Refresh trade proposals"}
			</ActionButton>
			{tradeOffersSwitch.toggle}
			{tradeOffersSwitch.value === "table" ? (
				<OfferTable
					assetCols={[
						{
							title: "You receive",
							sortSequence: ["desc", "asc"],
							sortType: "number",
						},
						{
							title: "You trade away",
							sortSequence: ["desc", "asc"],
							sortType: "number",
						},
					]}
					getAssetColContents={(offer) => {
						return [
							{
								value: (
									<SummaryTeam
										hideFinanceInfo
										hideTeamOvr
										showInlinePlayerInfo
										summary={offer.summary}
										t={offer.summary.teams[0]}
									/>
								),
								...getSearchSortValues(offer.players, offer.picks),
							},
							{
								value: (
									<SummaryTeam
										hideFinanceInfo
										hideTeamOvr
										showInlinePlayerInfo
										summary={offer.summary}
										t={offer.summary.teams[1]}
									/>
								),
								...getSearchSortValues(offer.playersUser, offer.picksUser),
							},
						];
					}}
					challengeNoRatings={challengeNoRatings}
					handleNegotiate={handleNegotiate}
					handleRemove={(i) => {
						// The table shows filteredOffers, so the index is into that list
						const tid = filteredOffers[i]!.tid;
						setRemovedTids((prevTids) => [...prevTids, tid]);
					}}
					offers={filteredOffers}
					salaryCap={salaryCap}
					salaryCapType={salaryCapType}
				/>
			) : (
				<>
					{filteredOffers.map((offer, i) => {
						return (
							<Offer
								key={offer.tid}
								challengeNoRatings={challengeNoRatings}
								onNegotiate={() => {
									handleNegotiate(offer);
								}}
								onRemove={() => {
									const tid = offer.tid;
									setRemovedTids((prevTids) => [...prevTids, tid]);
								}}
								salaryCap={salaryCap}
								salaryCapType={salaryCapType}
								teamInfo={teamInfoCache[offer.tid]!}
								hideTopTeamOvrs
								first={i === 0}
								{...offer}
							>
								<div className="d-flex gap-5">
									{offer.summary.teams.map((t, j) => {
										return (
											<div key={j}>
												<SummaryTeam
													hideFinanceInfo
													showInlinePlayerInfo
													summary={offer.summary}
													t={t}
												/>
											</div>
										);
									})}
								</div>
							</Offer>
						);
					})}
				</>
			)}
		</>
	);
};

export default TradeProposals;
